import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * iter175 — Pull-to-refresh wrapper for the home page.
 *
 * Mobile-native gesture: at scrollTop=0, dragging down past the
 * `triggerPx` threshold and releasing reloads the entire app
 * (window.location.reload(true)). This is the same UX recipients
 * expect from native iOS/Android apps and Twitter/Reddit/Discord
 * PWAs — vital because in PWA standalone display-mode the browser's
 * built-in pull-to-refresh is disabled.
 *
 * The implementation is dampened (50%) so the drag feels rubbery
 * rather than 1:1, and it ignores multi-touch / mid-scroll moves so
 * it never fights with vertical scrolling within the page.
 *
 * Desktop users see no indicator — `touchstart` simply never fires.
 */
const TRIGGER_PX = 80;     // Drop past this distance → refresh on release
const MAX_PULL = 140;      // Dampened cap so the indicator can't fly off-screen
const DAMP = 0.45;         // 0..1 — lower = more resistance

const PullToRefresh = ({ children, testId = 'pull-to-refresh' }) => {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const lockedH = useRef(false);   // Locked once we know this gesture is vertical-not-horizontal

  useEffect(() => {
    const onStart = (e) => {
      if (refreshing) return;
      if (e.touches.length !== 1) return;
      if ((window.scrollY || document.documentElement.scrollTop) > 0) return;
      startY.current = e.touches[0].clientY;
      lockedH.current = false;
    };

    const onMove = (e) => {
      if (refreshing) return;
      if (startY.current == null) return;
      // If the page started scrolling away from the top mid-gesture,
      // cancel — the user is mid-page, not pulling-to-refresh.
      if ((window.scrollY || document.documentElement.scrollTop) > 0) {
        startY.current = null;
        setPullY(0);
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        // Upward drag → nothing to do.
        setPullY(0);
        return;
      }
      if (!lockedH.current && dy < 6) return;  // tiny moves — wait until intent is clear
      lockedH.current = true;
      const damped = Math.min(dy * DAMP, MAX_PULL);
      setPullY(damped);
      // Once we're confident this is a pull-down gesture (>16px), stop
      // the page scroll from fighting us. Without this, iOS Safari
      // bounces and the indicator stutters.
      if (damped > 16 && e.cancelable) {
        try { e.preventDefault(); } catch (_e) { /* noop */ }
      }
    };

    const onEnd = () => {
      if (refreshing) return;
      const triggered = pullY >= TRIGGER_PX;
      startY.current = null;
      if (triggered) {
        setRefreshing(true);
        setPullY(TRIGGER_PX);
        // Reload the entire app — same as a hard browser refresh,
        // matches the user's spec ("refreshes the entire app").
        // setTimeout lets the indicator render the "spinning" frame
        // before the navigation tears down the React tree.
        setTimeout(() => {
          try { window.location.reload(); } catch (_e) { /* noop */ }
        }, 120);
      } else {
        setPullY(0);
      }
    };

    // We need passive:false so we can preventDefault on touchmove and
    // suppress the iOS rubber-band scroll behind us. Standard 3-event
    // touch handling.
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove,  { passive: false });
    window.addEventListener('touchend',  onEnd,   { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove',  onMove);
      window.removeEventListener('touchend',   onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [pullY, refreshing]);

  const progress = Math.min(pullY / TRIGGER_PX, 1);
  const indicatorOpacity = Math.min(progress * 1.2, 1);

  return (
    <div data-testid={testId} data-pull-to-refresh-root="1" className="relative">
      {/* Indicator pinned to the very top of the viewport. Slides
          down with the drag, then auto-hides on release. */}
      <div
        aria-hidden={pullY === 0}
        className="pointer-events-none fixed left-0 right-0 flex justify-center"
        style={{
          top: 0,
          transform: `translateY(${Math.max(pullY - 40, 0)}px)`,
          opacity: indicatorOpacity,
          transition: refreshing || startY.current == null ? 'transform 220ms ease, opacity 220ms ease' : 'none',
          zIndex: 60,
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className="mt-2 flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-[#1a1a1a] shadow-lg border border-gray-200 dark:border-white/10"
          style={{ transform: `rotate(${progress * 360}deg)` }}
        >
          <RefreshCw className={`w-5 h-5 text-[#586D52] ${refreshing ? 'animate-spin' : ''}`} />
        </div>
      </div>
      {/* Children get nudged down so the indicator appears to drag
          the content with the gesture. Snap back to 0 on release via
          the same transition. iter175bq — the `data-pull-to-refresh-
          inner` marker lets the collapsed-view CSS rules in App.css
          treat the two intermediary wrappers as transparent so
          `main > .min-h-screen` selectors continue to match the page
          content sitting one layer deeper. */}
      <div
        data-pull-to-refresh-inner="1"
        style={{
          transform: `translateY(${pullY * 0.6}px)`,
          transition: refreshing || startY.current == null ? 'transform 220ms ease' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
